import { DOCUMENT, Injectable, effect, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ModeService } from '../core/mode/mode.service';
import { TenantContextService } from '../core/tenant-context/tenant-context.service';

const DEFAULT_TITLE = 'SportDesk — Tu faci sport, noi restul';
const DEFAULT_DESCRIPTION =
  'Management de echipă pentru cluburi, antrenori și jucători — optimizat mobil.';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);
  private readonly mode = inject(ModeService);
  private readonly tenantCtx = inject(TenantContextService);

  init(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.applyFromRoute());

    // Tenant identity loads asynchronously (HTTP) after the first navigation, so
    // re-apply once the club name is known to title the tab/PWA after the club.
    effect(() => {
      this.tenantCtx.teamName();
      this.applyFromRoute();
    });
  }

  private applyFromRoute(): void {
    let r = this.route.snapshot;
    while (r.firstChild) r = r.firstChild;
    const data = r.data ?? {};
    const routeTitle = data['title'] as string | undefined;
    const description = (data['description'] as string | undefined) ?? DEFAULT_DESCRIPTION;

    const title = this.resolveTitle(routeTitle);

    this.title.setTitle(title);
    this.setMeta('description', description);
    this.setMeta('og:title', title, true);
    this.setMeta('og:description', description, true);
    this.setMeta('og:type', 'website', true);
    this.setCanonical();
  }

  /**
   * On a tenant subdomain the title is the club's name (e.g. "Cobra Karate"),
   * so the browser tab and the installed PWA are named after the club rather
   * than "SportDesk". A page-level route title is prefixed when one exists.
   */
  private resolveTitle(routeTitle?: string): string {
    if (this.mode.isTenant()) {
      const teamName = this.tenantCtx.teamName();
      if (teamName) return routeTitle ? `${routeTitle} · ${teamName}` : teamName;
    }
    return routeTitle ?? DEFAULT_TITLE;
  }

  private setMeta(name: string, content: string, property = false): void {
    const attr = property ? 'property' : 'name';
    const selector = `${attr}="${name}"`;
    if (this.meta.getTag(selector)) {
      this.meta.updateTag({ [attr]: name, content }, selector);
    } else {
      this.meta.addTag({ [attr]: name, content });
    }
  }

  private setCanonical(): void {
    const href = this.doc.location.origin + this.doc.location.pathname;
    let link = this.doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
